import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";

import {
  type PlaticaAuditada,
  useAuditoriaEstado,
  useAuditoriaPlaticas,
  usePlaticaAuditada,
} from "@/hooks/useAuditoriaPlaticas";

const ORIGEN: Record<string, string> = {
  chat: "Web",
  telegram: "Telegram",
  receta: "Reporte programado",
  correo: "Correo",
  automatic: "Automático",
  manual: "Incidente",
  proactive: "Proactivo",
};

function cuando(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—";
}

/** Auditoría de pláticas (ISO 27001): quién, cuándo, qué consultó y cuánto costó. */
export function AuditoriaPlaticasPage() {
  const estado = useAuditoriaEstado();
  const encendida = estado.data?.enabled === true;
  const [dias, setDias] = useState(30);
  const [usuario, setUsuario] = useState("");
  const [abierta, setAbierta] = useState<string | null>(null);
  const lista = useAuditoriaPlaticas({ dias, usuario }, encendida);
  const una = usePlaticaAuditada(abierta);

  if (estado.isPending) return <Loader2 className="size-5 animate-spin" />;
  if (!encendida) {
    return (
      <div className="max-w-xl text-sm text-muted-foreground">
        La auditoría de pláticas está apagada en este servidor (<code>AUDITORIA_PLATICAS_ENABLED</code>).
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <ShieldCheck className="size-5" /> Auditoría de pláticas con AROC
        </h1>
        <p className="text-sm text-muted-foreground">
          Todas las pláticas (web, Telegram, recetas, correo, incidentes). Cada consulta que hagas aquí queda en el
          registro de auditoría de administración.
        </p>
      </div>
      <div className="flex gap-3 text-sm">
        <label>
          Últimos días{" "}
          <input type="number" min={1} max={366} className="w-20 rounded border px-2 py-1" value={dias} onChange={(e) => setDias(Number(e.target.value) || 30)} />
        </label>
        <label>
          Usuario{" "}
          <input className="rounded border px-2 py-1" value={usuario} onChange={(e) => setUsuario(e.target.value.trim())} />
        </label>
        <span className="self-center text-muted-foreground">{lista.data ? `${lista.data.total} pláticas` : ""}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th>Cuándo</th>
            <th>Usuario</th>
            <th>Origen</th>
            <th>Plática</th>
            <th>Consultó</th>
            <th className="text-right">Costo</th>
          </tr>
        </thead>
        <tbody>
          {(lista.data?.items ?? []).map((p: PlaticaAuditada) => (
            <tr key={p.id} className="cursor-pointer border-t hover:bg-muted" onClick={() => setAbierta(p.id)}>
              <td>{cuando(p.started_at)}</td>
              <td>{p.dueno ?? "sistema"}</td>
              <td>{ORIGEN[p.trigger_type] ?? p.trigger_type}</td>
              <td className="max-w-xs truncate">{p.titulo || "—"}</td>
              <td className="text-xs">{p.tools.join(", ") || "—"}</td>
              <td className="text-right font-mono text-xs">
                {p.costo_usd == null ? "—" : `US$${p.costo_usd.toFixed(4)}`}
                {p.costo_incompleto && " *"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {abierta && (
        <div role="dialog" aria-label="Plática auditada" className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setAbierta(null)}>
          <div className="h-full w-full max-w-2xl overflow-auto bg-background p-5" onClick={(e) => e.stopPropagation()}>
            {una.isPending ? (
              <Loader2 className="size-5 animate-spin" />
            ) : una.data ? (
              <div className="space-y-2 text-sm">
                <div className="font-medium">
                  {una.data.dueno ?? "sistema"} · {ORIGEN[una.data.trigger_type] ?? una.data.trigger_type} ·{" "}
                  {cuando(una.data.started_at)}
                </div>
                {una.data.messages.map((m, i) => (
                  <div key={i} className="rounded border px-3 py-2">
                    <div className="text-xs text-muted-foreground">
                      {m.role}
                      {m.tool_name ? ` · ${m.tool_name}` : ""} · {cuando(m.timestamp)}
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-xs">{m.content}</pre>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-destructive">No se pudo abrir.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
